import { validationTranslationKeys } from '@va/constants';
import { FilterConnectors, useFiltersContext } from '@va/shared/feature-filters';
import { useAddNotification } from '@va/dashboard/util-hooks';
import { useTranslate } from '@va/localization';
import { getUserId } from '@va/standalone/shared/helpers';
import { FilterCondition } from '@va/types/filter-templates';
import { useFormik } from 'formik';
import { isNil } from 'lodash';
import { useEffect, useMemo } from 'react';
import * as yup from 'yup';
import { useVisitorSegmentsContext } from './VisitorSegmentsContext';
import { useCreateVisitorSegments, useUpdateVisitorSegments } from './api';
import { SegmentPayload, VisitorSegment } from './types';

export enum SegmentsFormFields {
  name = 'name',
  note = 'note',
}

type FormValues = { name: string; note: string; conditions: FilterCondition[] };

export const useSegmentsForm = () => {
  const { segments, segmentToEdit, mutateSegments } = useVisitorSegmentsContext();
  const { showErrorNotification, showSuccessNotification } = useAddNotification();
  const { appliedFilters, deleteAllAppliedFilters } = useFiltersContext();
  const translate = useTranslate();
  const userId = getUserId();
  const { execute: executeUpdateSegment } = useUpdateVisitorSegments();
  const { execute: executeCreateSegment } = useCreateVisitorSegments();

  const formValues = useMemo(
    () => ({ name: segmentToEdit?.name ?? '', note: segmentToEdit?.note ?? '', conditions: [] as FilterCondition[] }),
    [segmentToEdit],
  );

  const onFormSubmit = async (values: FormValues) => {
    const isCreateView = segmentToEdit === undefined;

    let payload: SegmentPayload = {
      name: values.name,
      conditions: { [FilterConnectors.and]: values.conditions },
    };

    if (values.note) {
      payload = { ...payload, note: values.note };
    }

    try {
      isCreateView
        ? await executeCreateSegment({ ...payload, createdBy: userId ?? null })
        : await executeUpdateSegment(segmentToEdit.id, payload);
      showSuccessNotification();
      if (isCreateView) {
        resetForm();
        deleteAllAppliedFilters();
      }
    } catch {
      showErrorNotification();
    } finally {
      mutateSegments();
    }
  };

  const { values, handleChange, setFieldValue, handleSubmit, errors, resetForm, validateField, dirty } = useFormik({
    onSubmit: onFormSubmit,
    initialValues: formValues,
    enableReinitialize: true,
    validationSchema: getValidationSchema(translate, segments, segmentToEdit),
  });

  useEffect(() => {
    setFieldValue(
      'conditions',
      appliedFilters
        .filter((filter) => filter.values.length !== 0 && filter.values[0] !== '' && !isNil(filter.values[0]))
        .map((filter) => {
          return { member: filter.name, operator: filter.operator, values: filter.values };
        }),
      false,
    ).then(() => {
      if (!dirty) return;
      setTimeout(() => {
        validateField('conditions');
      }, 50);
    });
  }, [appliedFilters, dirty, setFieldValue, validateField]);

  return { values, handleChange, setFieldValue, handleSubmit, errors };
};

function getValidationSchema(translate: Function, segments: Array<VisitorSegment>, segmentToEdit?: VisitorSegment) {
  return yup.object().shape({
    name: yup
      .string()
      .required(translate(validationTranslationKeys.required))
      .max(50, translate(validationTranslationKeys.maximumCharactersCount, { count: 50 }))
      .notOneOf(
        segments.filter((s) => s.name !== segmentToEdit?.name).map((s) => s.name),
        translate('all.segments.form.validation.nameAlreadyExists'),
      ),
    note: yup.string(),
    conditions: yup.array().required(translate('all.segments.form.validation.filtersRequired')),
  });
}
